import React, { useState, useCallback, useMemo } from 'react';
import { ReactComponent as FireIcon } from '../assets/fire.svg';
import { ReactComponent as LightBulb } from '../assets/lightbulb.svg';
import { ReactComponent as ArrowIcon } from '../assets/arrow.svg';
import { ReactComponent as ArrowLeftIcon } from '../assets/arrow-left.svg';
import { ReactComponent as CopyIcon } from '../assets/copy.svg';
import { ReactComponent as CrownIcon } from '../assets/crown.svg';
import AvatarGif from './AvatarGif';
import SkeletonLoader from './SkeletonLoader';
import { SuggestionPanelProps} from './types';
import { xtmCategories, xtmMoreSuggestions, xtrfCategories, xtrfMoreSuggestions } from './constants';


const SuggestionPanel: React.FC<SuggestionPanelProps> = React.memo(({
    onSuggestionClick,
    onSuggestionInsert,
    systemType = 'xtm',
    isLoading: externalIsLoading,
    firstName,
    hasLicenseAccess = true,
}) => {
    const [showMoreSuggestions, setShowMoreSuggestions] = useState(false);
    const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(null);

    const isLoading = externalIsLoading === true;

    const isRestrictedCategory = useCallback((categoryId: string, categoryTitle: string): boolean => {
        const restrictedIds = ['project-prioritization', 'issues-radar', 'project-management', 'client-queries', 'additional-queries', 'client-info', 'quick-actions'];
        const restrictedTitles = ['Prioritize projects', 'Identify issues', 'Client information'];
        return restrictedIds.includes(categoryId) || restrictedTitles.includes(categoryTitle);
    }, []);

    const categories = useMemo(() =>
        systemType === 'xtrf' ? xtrfCategories : xtmCategories,
        [systemType]
    );

    // Memoize the more suggestions based on systemType
    const moreSuggestions = useMemo(() => 
        systemType === 'xtrf' ? xtrfMoreSuggestions : xtmMoreSuggestions,
        [systemType]
    );

    // Copy button click handler - copies to clipboard
    const handleCopyClick = useCallback((e: React.MouseEvent, suggestion: string) => {
        e.stopPropagation();
        navigator.clipboard.writeText(suggestion);
    }, []);

    const handleSuggestionClick = useCallback((suggestion: string, categoryId?: string, categoryTitle?: string) => {
        if (categoryId && categoryTitle && isRestrictedCategory(categoryId, categoryTitle) && !hasLicenseAccess) {
            return;
        }
        
        if (onSuggestionInsert) {
            onSuggestionInsert(suggestion);
        }
        onSuggestionClick(suggestion);
    }, [onSuggestionInsert, onSuggestionClick, isRestrictedCategory, hasLicenseAccess]);

    const handleBackToMain = useCallback(() => {
        setShowMoreSuggestions(false);
        setSelectedSuggestion(null);
    }, []);

    const handleShowMoreSuggestions = useCallback(() => {
        setShowMoreSuggestions(true);
    }, []);

    return (
        <div className="suggestions-container" data-testid="suggestions-container">
            {isLoading ? (
                <>
                    <div className="greeting-container" data-testid="greeting-container-loading">
                        <div className="assistant-avatar" data-testid="assistant-avatar-loading">
                            <AvatarGif
                                key="stable-gif-key"
                                height={40}
                                width={40}
                                alt="Assistant Avatar"
                                isLoading={true}
                                data-testid="avatar-gif-loading"
                            />
                        </div>
                        <div className="greeting-content" data-testid="greeting-content-loading">
                            <div className="greeting-title" data-testid="greeting-title-loading">
                                <SkeletonLoader type="text" width={90} height={16} data-testid="greeting-title-skeleton" />
                            </div>
                            <div className="greeting-subtitle" data-testid="greeting-subtitle-loading">
                                <SkeletonLoader type="text" width={170} height={14} data-testid="greeting-subtitle-skeleton" />
                            </div>
                        </div>
                    </div>
                    <div className="suggestion-category" data-testid="suggestion-category-loading">
                        <div data-testid="suggestion-info-container-loading">
                            <div className="suggestion-info" data-testid="suggestion-info-loading">
                                <LightBulb data-testid="lightbulb-icon-loading" />
                                <SkeletonLoader type="text" width={280} height={12} data-testid="suggestion-info-text-skeleton" />
                            </div>

                            <div data-testid="suggestion-wrapper-container-loading">
                                <div className="suggestion-wrapper" data-testid="suggestion-wrapper-loading">
                                    <div className="suggestion-item" data-testid="suggestion-item-loading">
                                        <SkeletonLoader type="suggestion" width={200} data-testid="suggestion-item-skeleton" />
                                    </div>
                                    <button className="send-suggestion-button" data-testid="send-suggestion-button-loading">
                                        <ArrowIcon data-testid="arrow-icon-loading" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: '16px' }} data-testid="category-container-loading">
                            <div className="category-title" data-testid="category-title-loading">
                                <span className="category-icon" data-testid="category-icon-loading">
                                    <FireIcon data-testid="fire-icon-loading" />
                                </span>
                                <SkeletonLoader type="text" width={120} height={14} data-testid="category-title-skeleton" />
                            </div>
                            <div className="suggestion-list" data-testid="suggestion-list-loading">
                                {[1, 2, 3].map((index) => (
                                    <div className="suggestion-wrapper" key={index} data-testid={`suggestion-wrapper-loading-${index}`}>
                                        <div className="suggestion-item" data-testid={`suggestion-item-loading-${index}`}>
                                            <SkeletonLoader type="suggestion" width={200} data-testid={`suggestion-item-skeleton-${index}`} />
                                        </div>
                                        <button className="send-suggestion-button" data-testid={`send-suggestion-button-loading-${index}`}>
                                            <ArrowIcon data-testid={`arrow-icon-loading-${index}`} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="more-suggestions" data-testid="more-suggestions-loading">
                        <SkeletonLoader type="text" width={120} height={14} style={{ marginBottom: 0, marginTop: 8 }} data-testid="more-suggestions-skeleton" />
                    </div>
                </>
            ) : selectedSuggestion ? (
                <div className="selected-suggestion-container" data-testid="selected-suggestion-container">
                    <div className="back-button" onClick={handleBackToMain} data-testid="back-button">
                        <ArrowLeftIcon data-testid="back-arrow-icon" />
                         Back
                    </div>
                    <div className="selected-suggestion" data-testid="selected-suggestion">
                        <div className="suggestion-wrapper" data-testid="selected-suggestion-wrapper">
                            <div 
                                className="suggestion-item"
                                onClick={() => handleSuggestionClick(selectedSuggestion)}
                                title="Send"
                                data-testid="selected-suggestion-item"
                            >
                                {selectedSuggestion}
                            </div>
                            <button 
                                className="send-suggestion-button"
                                onClick={(e) => handleCopyClick(e, selectedSuggestion)}
                                title="Copy to clipboard"
                                data-testid="selected-suggestion-copy-button"
                            >
                                <CopyIcon data-testid="selected-suggestion-copy-icon" />
                            </button>
                        </div>
                    </div>
                </div>
            ) : showMoreSuggestions ? (
                <>
                    <div className="back-button" onClick={handleBackToMain} data-testid="back-to-main-button">
                        <ArrowLeftIcon data-testid="back-arrow-icon" />
                        Back
                    </div>
                    <div className="suggestion-category" data-testid="more-suggestions-category">
                        {moreSuggestions.map((category) => {
                            const isRestricted = isRestrictedCategory(category.id, category.title);
                            const isDisabled = isRestricted && !hasLicenseAccess;
                            
                            return (
                                <div key={category.id} data-testid={`more-category-${category.id}`} className={isDisabled ? 'category-disabled' : ''}>
                                    <div className={`category-title ${isDisabled ? 'disabled' : ''}`} data-testid={`more-category-title-${category.id}`}>
                                        <span className="category-icon" data-testid={`more-category-icon-${category.id}`}>
                                            {isDisabled ? <CrownIcon /> : category.icon}
                                        </span>
                                        {category.title}
                                    </div>
                                    <div className="suggestion-list" data-testid={`more-suggestion-list-${category.id}`}>
                                        {category.suggestions.map((suggestion, index) => (
                                            <div className={`suggestion-wrapper ${isDisabled ? 'disabled' : ''}`} key={`${category.id}-${index}`} data-testid={`more-suggestion-wrapper-${category.id}-${index}`}>
                                                <div
                                                    className={`suggestion-item ${isDisabled ? 'disabled' : ''}`}
                                                    onClick={() => handleSuggestionClick(suggestion, category.id, category.title)}
                                                    title={isDisabled ? "Advanced insights like priorities, issues and workflow analysis are available on paid plans." : "Send"}
                                                    data-testid={`more-suggestion-item-${category.id}-${index}`}
                                                    style={isDisabled ? { cursor: 'not-allowed' } : {}}
                                                >
                                                    {suggestion}
                                                </div>
                                                {!isDisabled && (
                                                    <button 
                                                        className="send-suggestion-button"
                                                        onClick={(e) => handleCopyClick(e, suggestion)}
                                                        title="Copy to clipboard"
                                                        data-testid={`more-copy-suggestion-button-${category.id}-${index}`}
                                                    >
                                                        <CopyIcon data-testid={`more-copy-icon-${category.id}-${index}`} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                <>
                    <div className="greeting-container" data-testid="greeting-container">
                        <div className="assistant-avatar" data-testid="assistant-avatar">
                            <AvatarGif
                                key="stable-gif-key"
                                height={40}
                                width={40}
                                alt="Assistant Avatar"
                                isLoading={isLoading}
                                data-testid="avatar-gif"
                            />
                        </div>
                        <div className="greeting-content" data-testid="greeting-content">
                            <div className="greeting-title" data-testid="greeting-title">Hello{firstName ? `, ${firstName},` : ''}</div>
                            <div className="greeting-subtitle" data-testid="greeting-subtitle">
                                how can I help you today?
                            </div>
                            <span className="greeting-policy">
								Please verify my auto-generated answers.
							</span>
                        </div>
                    </div>

                    <div className="suggestion-category" data-testid="suggestion-category">
                        <div data-testid="suggestion-info-container">
                            <div className="suggestion-info" data-testid="suggestion-info">
                                <LightBulb data-testid="lightbulb-icon" />
                                If you're ever wondering what I can help you with...
                            </div>

                            <div data-testid="what-can-xtm-agent-do-container">
                                <div className="suggestion-wrapper" data-testid="what-can-xtm-agent-do-wrapper">
                                    <div
                                        className="suggestion-item"
                                        onClick={() => handleSuggestionClick(`What can XTM Agent do?`)}
                                        title="Send"
                                        data-testid="what-can-xtm-agent-do-item"
                                    >
                                        What can XTM Agent do?
                                    </div>
                                    <button 
                                        className="send-suggestion-button"
                                        onClick={(e) => handleCopyClick(e, `What can XTM Agent do?`)}
                                        data-testid="what-can-xtm-agent-do-copy-button"
                                        title="Copy to clipboard"
                                    >
                                        <CopyIcon data-testid="what-can-xtm-agent-do-copy-icon" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {categories.map((category) => {
                            const isRestricted = isRestrictedCategory(category.id, category.title);
                            const isDisabled = isRestricted && !hasLicenseAccess;
                            
                            return (
                                <div key={category.id} data-testid={`category-${category.id}`} className={isDisabled ? 'category-disabled' : ''}>
                                    <div className={`category-title ${isDisabled ? 'disabled' : ''}`} data-testid={`category-title-${category.id}`}>
                                        <span className="category-icon" data-testid={`category-icon-${category.id}`}>
                                            {isDisabled ? <CrownIcon /> : category.icon}
                                        </span>
                                        {category.title}
                                    </div>
                                    <div className="suggestion-list" data-testid={`suggestion-list-${category.id}`}>
                                        {category.suggestions.map((suggestion, index) => (
                                            <div className={`suggestion-wrapper ${isDisabled ? 'disabled' : ''}`} key={`${category.id}-${index}`} data-testid={`suggestion-wrapper-${category.id}-${index}`}>
                                                <div
                                                    className={`suggestion-item ${isDisabled ? 'disabled' : ''}`}
                                                    onClick={() => handleSuggestionClick(suggestion, category.id, category.title)}
                                                    title={isDisabled ? "Advanced insights like priorities, issues and workflow analysis are available on paid plans." : "Send"}
                                                    data-testid={`suggestion-item-${category.id}-${index}`}
                                                    style={isDisabled ? { cursor: 'not-allowed' } : {}}
                                                >
                                                    {suggestion}
                                                </div>
                                                {!isDisabled && (
                                                    <button 
                                                        className="send-suggestion-button"
                                                        title="Copy to clipboard"
                                                        onClick={(e) => handleCopyClick(e, suggestion)}
                                                        data-testid={`copy-suggestion-button-${category.id}-${index}`}
                                                    >
                                                        <CopyIcon data-testid={`copy-icon-${category.id}-${index}`} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div 
                        className="more-suggestions"
                        onClick={handleShowMoreSuggestions}
                        data-testid="more-suggestions-button"
                    >
                        <span data-testid="more-suggestions-text">More suggestions</span>
                    </div>
                </>
            )}
        </div>
    );
});

export default SuggestionPanel;
